import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { loadUser, logout, selectUser } from '../../store/slices/authSlice';
import './NavUser.scss';

// Login button / logged-in user chip shown inside the navbar. On mount it asks
// the API for the current session so a returning visitor keeps their chip.
const NavUser = ({ mobile = false, variant }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  // Opening the account menu closes the notification bell panel, and vice
  // versa, via the navbar's existing event contract. The bell is untouched.
  const toggleMenu = () => {
    if (!open) window.dispatchEvent(new CustomEvent('av:close-notifications'));
    setOpen((o) => !o);
  };

  useEffect(() => {
    dispatch(loadUser());
  }, [dispatch]);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === 'Escape') setOpen(false);
    };
    const onBellOpened = () => setOpen(false);
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    window.addEventListener('av:close-notifications', onBellOpened);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('av:close-notifications', onBellOpened);
    };
  }, [open]);

  const doLogout = () => {
    setOpen(false);
    dispatch(logout());
    navigate('/');
  };

  if (!user?.username) {
    return (
      <div className={`nav-user ${mobile ? 'mobile' : ''}`} ref={boxRef}>
        <Link to="/login" className="nav-user-login" onClick={() => setOpen(false)}>
          <i className="fas fa-user-circle" aria-hidden="true"></i>
          <span>Login</span>
        </Link>
      </div>
    );
  }

  const initials = (user.full_name || user.first_name || user.username || '?').split(' ').slice(0, 2).map((s) => s.charAt(0).toUpperCase()).join('');

  return (
    <div className={`nav-user ${mobile ? 'mobile' : ''}${variant === 'icon' ? ' nav-user--icon-wrap' : ''} has-session`} ref={boxRef}>
      {variant === 'icon' ? (
        <button
          type="button"
          className={`nav-user-icon${open ? ' active' : ''}`}
          aria-label={user.full_name || user.username || 'Account'}
          aria-expanded={open}
          aria-haspopup="true"
          title="Account"
          onClick={toggleMenu}
        >
          <i className="fas fa-user-circle" aria-hidden="true"></i>
        </button>
      ) : (
        <button
          type="button"
          className="nav-user-chip"
          aria-expanded={open}
          aria-haspopup="true"
          onClick={toggleMenu}
        >
          <span className="nav-user-avatar" aria-hidden="true">{initials || 'U'}</span>
          <span className="nav-user-name">{user.full_name || user.first_name || 'Devotee'}</span>
          <i className={`fas fa-chevron-down nav-user-chev ${open ? 'open' : ''}`} aria-hidden="true"></i>
        </button>
      )}

      {open && (
        <div className="nav-user-menu">
          <div className="nav-user-menu-head">
            <strong>{user.full_name || user.first_name || user.username}</strong>
            <span>{user.email || user.username}</span>
          </div>
          {user.is_staff && (
            <Link to="/admin" className="nav-user-item" onClick={() => setOpen(false)}>
              <i className="fas fa-tachometer-alt" aria-hidden="true"></i> Admin Dashboard
            </Link>
          )}
          <button type="button" className="nav-user-item nav-user-logout" onClick={doLogout}>
            <i className="fas fa-sign-out-alt" aria-hidden="true"></i> Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default NavUser;